import { formatMoney } from "./currency.js";
/**
 * 收費模型
 * ═══════════════════════════════════════════════════════════════
 *
 * 本平台**不抽成**。接案者成交多少就拿多少，平台只收會員費。
 *
 * ── 為什麼是會員制而不是抽成制 ──────────────────────────────
 *
 * 抽成制有一個結構性的問題：平台的收入與案件金額綁在一起，
 * 於是平台會有動機推高成交金額、並且阻止雙方私下成交
 * （因為那等於逃掉抽成）。多數接案平台因此把「站外聯絡」列為
 * 禁止事項，甚至過濾訊息裡的電話與 email —— 這對使用者是純粹的損失。
 *
 * 會員制把這個動機拿掉：平台的收入是固定的，
 * 不會因為你這個月接了一個大案子就多收你錢。
 *
 * 代價是接案者要先付費才能投標，對「一年只接一兩個案子」的人不划算。
 * 三個月免費期就是為了讓人先確認自己屬於哪一種。
 *
 * ── 三個地方必須用同一個函式 ────────────────────────────────
 *
 * 前端顯示的「你實收多少」、contracts 服務實際入帳、對帳報表，
 * 三個地方都只能從這裡取值，否則遲早會對不起來。
 */

import type { CurrencyCode } from "./types.js";

/*
  這裡原本是 FREE_PROPOSAL_QUOTA = 5（五次免費提案）。

  刪掉的理由不是它錯，是它從來沒有生效過，而且不會生效了 ——
  開站優惠期間走的是時間窗，優惠結束後走的是「首購買五次成交」
  （見 FIRST_PURCHASE_WINS）。額度制夾在中間，唯一的作用是讓
  「我什麼時候要付錢」這個問題多一個答案。

  它留下的成本是真的：identity 有一組專屬的 SQL 篩選分支、
  contracts 有一條扣減路徑（而且是可以並行繞過的）、
  八個語系各有四條文案、fees.ts 有一整套狀態。
  維護一條永遠不跑的路徑，代價比刪掉它高。

  要找回來的話，git 記得。
*/

/**
 * 開站優惠：2026-09-01 到 12-01 免費。
 * ═══════════════════════════════════════════════════════════════
 *
 * 這三個月內，接案者不受付費限制 ——
 * 驗證過信箱（與手機）就能投標，也能主動聯絡發案方。
 * 十二月一日一到免費就結束：要繼續投標就得成為付費會員。
 *
 * 時間窗是全站共用的，不是每個人各自的三個月 ——
 * 十月才註冊的人只剩兩個月。詳見 TRIAL_ENDS_AT。
 *
 * 發案方（發包商）不在這個倒數裡，他們永遠免費 ——
 * 平台缺的是需求不是供給，向發案方收費等於向自己最稀缺的資源收費。
 * 這一點寫在 decideInquiry 的 as === "client" 那一行。
 *
 * ── 時間制的顧慮還在，這是取捨 ──────────────────────────────
 *
 * 被刪掉的額度制主張「計價的單位要跟價值的單位一致」，
 * 而且明白寫過時間制的壞處：倒數不管平台有沒有給你價值都在走。
 * 那個顧慮沒有消失：
 *
 *   額度制成立的前提是「五次投標足以看出平台有沒有用」。
 *   開站當下案件量接近零，五次投完全部沒有回音是常態 ——
 *   那時使用者撞到的不是試用期結束，是一堵牆。
 *
 *   三個月給的是「等案件長出來」的時間，同時給平台一個期限：
 *   十二月一日之前要讓案件量起來，否則收不到錢。壓力在對的一邊。
 *
 * 優惠結束後接手的是 FIRST_PURCHASE_WINS ——「第一筆會員費買的是
 * 五次成交，不是一個月」。所以倒數空轉的問題只存在於這三個月，
 * 付錢之後那個顧慮就不適用了：接不到案子，期限就不會開始算。
 *
 * ── 怎麼提前結束或延長 ──────────────────────────────────────
 *
 * 改 TRIAL_ENDS_AT，就這一個地方。
 * 早一版還有一個 LAUNCH_FREE_FOR_FREELANCERS 布林開關 ——
 * 兩個機制表達同一件事，於是「日期到了但開關還開著」
 * 這種矛盾狀態是存在的，而沒有人說得出那時候應該怎樣。
 */
export function launchOfferRunning(
  membership: Pick<Membership, "startedAt" | "trialEndedAt"> | null | undefined,
  now = new Date(),
): boolean {
  /*
    免費期改成每人各自計算之後，「開站優惠還在跑嗎」這個問題
    就不再有全站統一的答案 —— 它一定要問「對誰而言」。

    這個簽章的改變是刻意的：讓每一個呼叫端被編譯器逼著回答
    那個問題，而不是繼續沿用一個已經沒有意義的全域狀態。
  */
  return trialDaysLeft(membership, now) > 0;
}

/**
 * 手機驗證是不是投標與主動聯絡的必要條件。
 * ═══════════════════════════════════════════════════════════════
 *
 * 已啟用。前提（Twilio 的設定）在 2026-08-23 完成：
 * TWILIO_ACCOUNT_SID、TWILIO_VERIFY_SERVICE_SID、TWILIO_AUTH_TOKEN
 * 都已經設在 identity 上，Verify Service 也建好了。
 *
 * 要再關掉的話改回 false 就好，前後端一起生效。
 *
 * ── 為什麼需要這個開關 ──────────────────────────────────────
 *
 * identity 回的 phoneVerified 是 Boolean(phone_verified_at)，
 * 而在手機驗證上線之前，那一欄對**每一個現有使用者**都是 NULL。
 * 也就是說：這道關卡一啟用，全站所有接案者同時失去投標能力，
 * 而他們唯一的解法（去驗證手機）在 Twilio 設好之前是壞的。
 *
 * 那不是一道關卡，那是一次全站停擺。
 *
 * 所以順序必須是：先部署（開關關著）→ 設好 Twilio →
 * 才翻開關。這個順序已經走完了。
 *
 * ── 啟用當下所有既有使用者都會被擋 ──────────────────────────
 *
 * 那不是 bug，是這個規則的定義：沒驗手機就不能接案。
 * 但他們是在沒有這條規則的時候註冊的，所以**要主動通知**，
 * 不要讓他們在下一次想投標時才發現。
 * 帳號設定的第一個區塊就是驗證狀態，一分鐘可以完成。
 *
 * ── 為什麼是常數而不是環境變數 ──────────────────────────────
 *
 * 前後端要給同一個答案。放環境變數的話，前端拿不到，
 * 就會出現「畫面說可以、送出說不行」—— 那正是這個檔案開頭
 * 反覆強調要避免的事。
 */
export const REQUIRE_PHONE_VERIFICATION = true;

/**
 * 證件驗證是不是投標與主動聯絡的必要條件。
 * ═══════════════════════════════════════════════════════════════
 *
 * 三道驗證的性質不一樣，這一道特別重：
 *
 *   信箱   自動、幾秒鐘
 *   手機   自動、一分鐘
 *   證件   **要人審核**，而人會睡覺
 *
 * 所以打開它等於承諾：有人會在合理時間內把待審佇列清掉。
 * 沒有那個承諾的話，這道關卡的實際效果是「新來的接案者什麼都不能做，
 * 而且不知道要等多久」—— 那比沒有這道關卡更糟。
 *
 * ── 全站一致 ────────────────────────────────────────────────
 *
 * 投標、主動聯絡、看案件內文、繳會費 —— 同一組門檻。
 *
 * 分開設會讓使用者在不同頁面撞到不同的牆，而他無法從中推論
 * 「我到底還差什麼」。一致的門檻只要說一次：三道都過，全部解鎖。
 *
 * 佇列積壓時把這個常數改成 false 就能先放行，前後端一起生效。
 */
export const REQUIRE_ID_VERIFICATION = true;

/**
 * 證件驗證之前可以先做幾件事。
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼要有這個額度 ──────────────────────────────────────
 *
 * 上面那段註解自己指出了問題：打開 REQUIRE_ID_VERIFICATION 等於
 * 承諾「有人會在合理時間內把待審佇列清掉」。
 *
 * 而站方是一到兩個人。每天三十件就是一個半小時，沒有淡旺季、
 * 不能積壓、週末也一樣。一個人請假一週，回來是五百件的佇列
 * 加一批已經走掉的人 —— 他們還沒體驗過這裡有什麼，
 * 沒有理由等你。
 *
 * 但它給的解法是二元的：整個關卡開或關。**分級比二元好。**
 *
 *   信箱驗證   瀏覽、收藏
 *   手機驗證   可以投標與主動聯絡，但只有這個額度
 *   證件驗證   解除額度，並取得「已驗證身分」徽章
 *
 * 效果是證件審核從「擋在門口」變成「想要更多才做」。佇列自然
 * 變小，而且排隊的人已經在用平台了 —— 他等得起，因為他已經
 * 知道自己在等什麼。
 *
 * ── 為什麼是終身額度而不是每月 ──────────────────────────────
 *
 * 每月要一個重置的排程，而「不需要有人顧」正是這整件事的重點。
 * 一個會壞掉、會忘記部署、會在時區邊界出錯的 cron，為的是給一個
 * 本來就該去驗證的人更多次數 —— 不划算。
 *
 * 而且終身額度講的話比較誠實：「你可以先試三次」，而不是
 * 「你每個月都可以不驗證」。後者會讓一群人永遠停在未驗證狀態，
 * 而證件徽章是這個平台除了評價之外唯一的信任來源。
 *
 * ── 投標與主動聯絡共用同一個額度 ────────────────────────────
 *
 * 上面寫著「全站一致：分開設會讓使用者在不同頁面撞到不同的牆」。
 * 那句話在這裡仍然成立 —— 兩邊各自一個額度的話，使用者無法從
 * 「我還剩幾次」推論自己實際上還能做什麼。
 */
/*
  ── 改成 0：沒有驗證就不能接洽客戶 ──────────────────────────

  上面那整段論證的是「分級比二元好」，而它在**降低流失**這件事上
  是對的：一個還沒體驗過平台的人沒有理由等你審核。

  但它換來的東西是：一個未經身分驗證的人可以直接私訊三位真人。
  而這個平台不碰金流、不做仲裁 —— 出事之後能做的只有停權與留紀錄，
  錢追不回來。在那個前提下，「身分驗證」是平台唯一真正提供的保護，
  給它開三次後門等於讓那個保護對每一個新帳號都失效一次。

  三次不是小數目：批量註冊一百個帳號就是三百次冷開發。

  代價要說清楚，不是假裝沒有：新註冊的人在通過審核前什麼都不能做，
  而審核由一到兩個人手動處理。這條規則要成立，審核的速度就是
  平台的生死線 —— 上面那段關於佇列的分析仍然完全有效，
  只是現在它變成一個**營運**問題，而不是一個可以用額度繞過的問題。
*/
export const PRE_ID_ALLOWANCE = 0;

/**
 * 首次繳費買的是「五次成交」，不是一個月。
 * ═══════════════════════════════════════════════════════════════
 *
 * 第一筆會員費不啟動任何倒數。它給的是五次「合約成立」的額度，
 * 用完之後才開始按月或按年計費。
 *
 * ── 為什麼單位是成交而不是時間或投標 ────────────────────────
 *
 * 時間最糟：不管平台有沒有給你價值，那個倒數都在走。
 * 一個人付了錢、等了一個月、什麼都沒發生 —— 我們收了錢卻什麼
 * 都沒給，而且他不會續約。
 *
 * 投標次數也不對：投標對接案者沒有價值，接到案子才有。
 * 在案件量還少的市場裡，五次投標可能一天就燒完而且全部沒有回音。
 *
 * 成交是唯一一個「平台真的做到了什麼」的信號。
 * 所以承諾可以講得非常直接：接不到案子，期限就不會開始算。
 *
 * ── 對平台的意義 ────────────────────────────────────────────
 *
 * 它會自己轉換。流動性起來之後，五次成交很快就用完，
 * 使用者自然進入月費 —— 不需要我們之後再做一次決定，
 * 也不需要挑一個時間點去改規則。
 */
export const FIRST_PURCHASE_WINS = 5;


/**
 * 開站優惠的時間窗：2026-09-01 到 2026-12-01（台北時間）。
 * ═══════════════════════════════════════════════════════════════
 *
 * 這是**全站同一段時間**，不是每個人各自的三個月。
 * 十月一日才註冊的人只剩兩個月，十一月才來的只剩一個月 ——
 * 大家在同一天結束。
 *
 * ── 為什麼是固定窗口而不是每人三個月 ────────────────────────
 *
 * 因為這是開站優惠，不是試用期。兩者的差別在於它在說什麼：
 *
 *   每人三個月 = 「產品給你三個月試用」，那是一個永久的產品特性，
 *     每個新註冊的人都要再給一次，而且它獎勵晚點來的人
 *     （反正什麼時候來都是三個月）。
 *
 *   固定窗口 = 「現在進來的人有這個好處」，早來的人拿得多。
 *     它給的是現在就註冊的理由，而那正是開站需要的東西。
 *
 * 對外也好講：一個所有人都一樣的日期，不必解釋「你的三個月從哪天算」。
 *
 * ── 十二月一日之後會自動發生什麼 ────────────────────────────
 *
 * 不需要改程式、也不需要部署：日期一過，membershipState 就不再回
 * trial，接案者要投標就得付費。開關留著是為了提前收掉優惠或延長，
 * 不是為了讓某個人記得在那天手動翻它。
 *
 * ── 為什麼寫成帶時區位移而不是 Z ────────────────────────────
 *
 * 開站日講的是台北時間的 9/1，換算 UTC 是 8/31 16:00。
 * 直接寫 Z 會讓台灣使用者少掉八小時。
 */
/**
 * 免費期：從**註冊那一天**起算 90 天。
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼從固定日期改成每人各自計算 ────────────────────────
 *
 * 原本是全站共用的時間窗（2026-09-01 到 12-01）。那個設計的
 * 論點是「給平台一個期限：十二月一日之前要讓案件量起來，
 * 否則收不到錢，壓力在對的一邊」—— 那是好論點，但它有一個
 * 致命的副作用：
 *
 *   **所有人的免費期在同一天結束。**
 *
 * 假設轉換率一成（對一個活動量還很低的平台已經樂觀），那表示
 * 九成的供給端在同一個早上失去投標能力。而一個雙邊市場在一夜
 * 之間失去供給，就沒有東西可以賣了 —— 還在的發案方看得到。
 *
 * 每人 90 天之後，流失變成涓流而不是斷崖。這件事對還沒有規模的
 * 平台是生死問題，對 Upwork 不是。
 *
 * ── 第二個理由：越接近截止日，那句話越像誘餌 ──────────────
 *
 * 文案寫「免費到 12 月 1 日」。11 月 28 日註冊的人得到三天，
 * 而他讀到的是同一句話。對一個主打「不抽成的誠實平台」來說，
 * 那個落差傷得比省下的錢多。
 *
 * ── 第三個理由：模式本來就已經不一致 ──────────────────────
 *
 * FIRST_PURCHASE_WINS 說「第一筆會員費買的是五次成交，不是
 * 一個月」。付費那一側已經是價值計價，只有試用還是時間計價。
 *
 * ── 平台的期限沒有消失，只是不再寫進使用者的條款 ──────────
 *
 * 原本的設計用使用者的權益當自己的鬧鐘，而代價由他們付。
 * 要給自己壓力的話，自己設一個內部期限就好。
 */
export const TRIAL_DAYS = 90;

/**
 * 開站優惠的報名截止日。
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 兩個機制各管一件事，不重疊 ────────────────────────────
 *
 *   LAUNCH_SIGNUP_ENDS_AT   **誰有資格** —— 在這之前註冊的人
 *   TRIAL_DAYS              **拿到多少** —— 從各自的註冊日起算
 *
 * 這是把兩種設計合併的結果，而合併之後兩邊的缺點都不見了：
 *
 *   固定窗口的缺點是所有人同一天到期（斷崖），以及晚來的人
 *   拿得少（那句「免費到 12/1」對他等於誘餌）。
 *
 *   純 90 天的缺點是平台失去自己的期限 —— 原本的設計刻意用
 *   一個結束日給自己壓力：「12/1 之前要讓案件量起來」。
 *
 * 合併之後：截止日管的是招募的壓力（在那之前要把人找進來），
 * 90 天管的是每個人拿到的東西。到期日因此散開在各自註冊日的
 * 90 天後，不會擠在同一天。
 *
 * ── 這句話在任何時候對任何人都是真的 ────────────────────────
 *
 *   「2026 年 12 月 1 日前註冊，享 90 天免費投標」
 *
 * 11 月 28 日註冊的人拿到的是完整的 90 天，不是三天。
 * 而 12 月 2 日來的人看到的是一句誠實的「優惠已結束」，
 * 不是一句寫著免費卻沒有內容的話。
 *
 * ── 早一版還有一個 TRIAL_STARTS_AT ────────────────────────
 *
 * 拿掉了。開站日對「這個人有沒有資格」沒有影響 —— 在開站前
 * 註冊的人本來就更早，而他們的 90 天照樣從自己的註冊日算。
 * 多一個沒有作用的常數只會讓下一個讀的人以為它有作用。
 */
export const LAUNCH_SIGNUP_ENDS_AT = "2026-12-01T00:00:00+08:00";


/**
 * 平台抽成：0。
 *
 * 這個常數保留而不是刪掉，是因為合約仍然會記錄
 * fee_bps_snapshot —— 成交當下的費率快照。
 * 現在的值永遠是 0，但快照機制留著：
 * 將來若真的要對某類交易收費，已成交的舊合約不會被回溯影響。
 */
export const BASE_FEE_BPS = 0;

// ══════════════════════════════════════════════════════════════════
// 會員費
// ══════════════════════════════════════════════════════════════════

/**
 * 會員費
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 每個市場各自訂價，不是換算 ──────────────────────────────
 *
 * 這個站是「各國在地的接案系統」，而一個換算出來的價格看起來就像
 * 換算出來的：€11.04、£9.48、NT$387。沒有人這樣標價，而那個零頭
 * 會讓使用者意識到「這是別的地方的價格翻過來的」。
 *
 * 所以每個幣別各自寫一個乾淨的數字。台灣是 NT$390 —— 定價是從
 * 台灣訂的，其餘各國取相近的購買力再取整。
 *
 * ── 之前這裡是錯的 ──────────────────────────────────────────
 *
 * 舊版只存美元，而畫面上的 money() 是把金額當成**市場自己的幣別**
 * 在格式化的。於是台灣的定價頁把 US$19 的 1900 分當成台幣，
 * 顯示成 NT$19 —— 一年的會員費看起來像一杯飲料。
 *
 * 型別是對的（都是 number）、測試是綠的、畫面畫得出來。
 * 這和之前那個 32.5 倍的價格 bug 是同一類：兩個各自合理的東西
 * 對「這個數字是什麼幣別」有不同的假設。
 *
 * ── 年繳等於付十個月 ────────────────────────────────────────
 *
 * 買一年送兩個月。兩個月的折扣足以讓人認真考慮，
 * 又不至於低到讓月繳看起來像懲罰。
 */
export const MEMBERSHIP_MONTHLY_MINOR: Record<CurrencyCode, number> = {
  /*
    台灣是定價的錨點：NT$599 ／月。其餘各國取相近的等值再取整 ——
    大約都落在 US$18–19 之間，但每一個都是那個幣別自己看得順眼的
    數字，不是 599 除以匯率的結果。
  */
  TWD: 59_900, // NT$599
  USD: 1_800, // US$18
  EUR: 1_700, // €17
  GBP: 1_500, // £15
  JPY: 290_000, // ¥2,900
  SGD: 2_500, // S$25
  AUD: 2_800, // A$28
};

/** 年繳＝十個月。買一年送兩個月 */
export const MEMBERSHIP_YEARLY_MONTHS = 10;

/**
 * 美元的值另外留一個常數。
 *
 * 跨市場比較（例如後台的營收統計）需要一個共同的尺，
 * 而那把尺是美元 —— 但**畫面上絕對不要用它**，
 * 畫面要用 priceFor(market)。
 */
export const MEMBERSHIP_MONTHLY_USD_MINOR = MEMBERSHIP_MONTHLY_MINOR.USD;
export const MEMBERSHIP_YEARLY_USD_MINOR =
  MEMBERSHIP_MONTHLY_USD_MINOR * MEMBERSHIP_YEARLY_MONTHS;

/**
 * 一個案子最低可以開多少（該市場的幣別，major 單位）。
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼不能是「5」 ──────────────────────────────────────
 *
 * 原本的下限是 5，而且文案寫著「至少 5 美元」—— 但實際存進去的
 * 數字是**市場自己的幣別**。所以台灣的下限實際上是 NT$5。
 *
 * 一個 NT$5 的案子不是便宜，是一個錯誤或一個測試 —— 而它會出現在
 * 案件列表上，被每一個接案者看到。
 *
 * ── 這不是「合理報價」的判斷 ────────────────────────────────
 *
 * 平台不替雙方決定價格。這個數字只是「一眼看得出是打錯的」那條
 * 界線 —— 少打一個零還是過得了，多打三個零也過得了。
 * 真正的價格判斷留給那兩個人。
 */
export const MIN_BUDGET_MAJOR: Record<CurrencyCode, number> = {
  TWD: 500,
  USD: 20,
  EUR: 20,
  GBP: 15,
  JPY: 3_000,
  SGD: 25,
  AUD: 30,
};

/** 找不到幣別時用美元的值 —— 回 0 會等於沒有下限 */
export function minBudgetFor(currency: CurrencyCode | string): number {
  return MIN_BUDGET_MAJOR[currency as CurrencyCode] ?? MIN_BUDGET_MAJOR.USD;
}

export type BillingCycle = "monthly" | "yearly";

export interface MembershipPrice {
  cycle: BillingCycle;
  usdMinor: number;
  /** 換算成每月多少，用於「年繳等於每月 $X」的比較 */
  perMonthUsdMinor: number;
  /** 年繳相對月繳省下的比例（0–1），月繳為 0 */
  savingRatio: number;
}

export const MEMBERSHIP_PRICES: Record<BillingCycle, MembershipPrice> = {
  monthly: {
    cycle: "monthly",
    usdMinor: MEMBERSHIP_MONTHLY_USD_MINOR,
    perMonthUsdMinor: MEMBERSHIP_MONTHLY_USD_MINOR,
    savingRatio: 0,
  },
  yearly: {
    cycle: "yearly",
    usdMinor: MEMBERSHIP_YEARLY_USD_MINOR,
    perMonthUsdMinor: Math.round(MEMBERSHIP_YEARLY_USD_MINOR / 12),
    savingRatio: 1 - MEMBERSHIP_YEARLY_MONTHS / 12,
  },
};

/**
 * 某個幣別、某個週期要付多少 —— **以那個幣別的 minor 單位**。
 *
 * 畫面上一律用這一支。傳 usdMinor 給 money() 是錯的：
 * money() 會把它當成市場自己的幣別，於是台灣看到 NT$12。
 */
export function membershipPriceMinor(
  currency: CurrencyCode,
  cycle: BillingCycle,
): number {
  const monthly = MEMBERSHIP_MONTHLY_MINOR[currency] ?? MEMBERSHIP_MONTHLY_MINOR.USD;
  return cycle === "yearly" ? monthly * MEMBERSHIP_YEARLY_MONTHS : monthly;
}

/**
 * 會員費的顯示字串，給文案裡的 {monthly} 與 {yearly} 用。
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼文案不能寫價格 ────────────────────────────────────
 *
 * 價格曾經直接寫在文案裡：中文「每月 NT$599」、日文「月額 ¥2,900」、
 * 英文「US$18 a month」。那三句各自只對一個市場是對的。
 *
 * 國際站之後，法國市場的中文頁寫著 NT$599、台灣市場的英文頁寫著
 * US$18 —— 而英文那一份還是另外十四種語言的翻譯來源，所以德國、
 * 韓國、越南的頁面全部寫著美金價格，不管那個市場收什麼幣別。
 *
 * 文案只寫佔位符，數字由市場的幣別算出來，和定價頁用的是同一份表。
 */
export function membershipPriceLabels(
  currency: CurrencyCode,
  locale = "en",
): { monthly: string; yearly: string } {
  const fmt = (minor: number) => formatMoney(minor, currency, currency, locale);
  return {
    monthly: fmt(membershipPriceMinor(currency, "monthly")),
    yearly: fmt(membershipPriceMinor(currency, "yearly")),
  };
}

/** 年繳攤成每月是多少（該幣別的 minor 單位） */
export function membershipPerMonthMinor(
  currency: CurrencyCode,
  cycle: BillingCycle,
): number {
  const monthly = MEMBERSHIP_MONTHLY_MINOR[currency] ?? MEMBERSHIP_MONTHLY_MINOR.USD;
  return cycle === "yearly" ? Math.round((monthly * MEMBERSHIP_YEARLY_MONTHS) / 12) : monthly;
}

// ══════════════════════════════════════════════════════════════════
// 會員狀態
// ══════════════════════════════════════════════════════════════════

/*
  收費模型只有兩段，狀態機也只該有這幾個。

    開站優惠（到 TRIAL_ENDS_AT）      → trial
    付費中（含首購的成交額度）        → active
    優惠結束、從來沒付過費            → exhausted
    付過費但已到期                    → expired
    沒有接案者檔案（發案方）          → none
*/
export type MembershipState =
  /** 開站優惠的免費期內，到 2026-12-01 為止（見 TRIAL_ENDS_AT） */
  | "trial"
  /** 已付費且在期限內（含首購的成交額度階段） */
  | "active"
  /** 開站優惠結束了，而且從來沒付過費 */
  | "exhausted"
  /** 付過費但已到期 */
  | "expired"
  /** 發案方，或還沒有接案者檔案 */
  | "none";

export interface Membership {
  /** 加入平台的時間（ISO） */
  startedAt: string;
  /**
   * 免費期提前結束的時間。
   *
   * ── 規則：接到案子就取消 ────────────────────────────────────
   *
   * 註冊送 90 天，但接到第一個案子的那一刻，那 90 天到此為止。
   *
   * 90 天的用途是「讓你有機會證明這裡有用」。一個人接到案子的
   * 那一刻，那件事已經被證明了 —— 繼續免費只是在延後一個他已經
   * 願意做的決定。
   *
   * 反過來，一個 90 天內一件都沒接到的人，繼續向他收費才是奇怪的：
   * 他還沒有從這裡拿到任何東西。而他有第二條路 —— 介紹朋友，
   * 一個人多一個月（見 shared/referral.ts）。
   *
   * ── 為什麼是一個獨立的時間戳 ────────────────────────────────
   *
   * 不是用「有沒有合約」去算。合約可以被取消、刪除、或者資料重置，
   * 而免費期一旦結束就不該因此復活 —— 存時間戳，那件事就只會
   * 發生一次。
   */
  trialEndedAt?: string | null;
  /** 付費會員的到期時間（ISO）。續約就是把它往後延。 */
  paidUntil?: string;
  /** 下次自動續約的週期。null 表示已取消自動續約。 */
  cycle?: BillingCycle | null;
  /**
   * 首購的成交額度還剩幾次。
   *
   * null 或 undefined 代表不在這個階段（還沒付費，或已經用完並
   * 轉成按期間計費）。大於 0 時會員資格有效，而且 paidUntil
   * 還沒開始算 —— 這兩件事必須一起看，見 membershipState。
   */
  firstPurchaseWinsLeft?: number | null;
  /**
   * 信箱驗證過了沒。
   *
   * 免費不等於不設防：一個假信箱能投標的話，
   * 開站優惠就變成一個無限量的垃圾提案管道。
   */
  emailVerified?: boolean;
  /**
   * 手機驗證過了沒。
   *
   * 信箱擋的是「換一個假地址」，手機擋的是「用多個信箱
   * 重複註冊同一個人」—— 信箱免費且無限，門號不是。
   * 兩道一起才擋得住批量註冊。
   *
   * 順序上信箱先於手機：信箱是唯一能送出合約、發票與帳號救援的管道，
   * 而簡訊要花錢，先確認這是一個真實可聯絡的人再寄。
   */
  phoneVerified?: boolean;
  /**
   * 證件驗證過了沒。
   *
   * 與另外兩道不同，這一道要人審核 —— 所以它同時是「這個人是真的」
   * 和「我們花了時間確認過」兩件事。發案方看到的徽章值錢，
   * 就是因為它不能自助取得。
   *
   * 同樣用 === false 判斷：沒帶的舊資料視為通過，
   * 跨服務部署的那幾分鐘不該把所有人鎖住。
   */
  idVerified?: boolean;
  /** 站方人工核准過。第四關，見 identity/migrations/0032 */
  approved?: boolean;
  /**
   * 證件驗證之前已經用掉幾次額度（投標 + 主動聯絡合計）。
   *
   * 沒帶時視為 0 —— 和其他欄位一樣，跨服務部署的那幾分鐘不該把人
   * 鎖住，而這個方向的預設值是比較寬鬆的那一邊。
   *
   * 通過證件驗證之後這個數字就不再被讀取，所以不需要歸零。
   */
  preIdUsed?: number;
}

/**
 * 目前的會員狀態。
 *
 * 順序有意義，而且付費一定要排在優惠前面：
 * 一個已經付費、但優惠期剛好結束的人，順序寫反就會被判成到期。
 */
export function membershipState(
  membership: Membership | null | undefined,
  now = new Date(),
): MembershipState {
  if (!membership) return "none";

  /*
   * 首購的成交額度優先於一切。
   *
   * 放在 paidUntil 之前判斷，因為這個階段的 paidUntil 是空的 ——
   * 倒數還沒開始。順序寫反的話首購會員會被判成 exhausted。
   */
  if ((membership.firstPurchaseWinsLeft ?? 0) > 0) return "active";

  if (membership.paidUntil && new Date(membership.paidUntil).getTime() > now.getTime()) {
    return "active";
  }

  if (trialDaysLeft(membership, now) > 0) return "trial";

  // 付過費但過期了，跟從來沒付過，給的訊息不一樣
  return membership.paidUntil ? "expired" : "exhausted";
}

/**
 * 開站優惠還剩幾天。已經結束或還沒開始都回 0。
 *
 * 不看 membership —— 免費期是全站同一段時間，跟這個人什麼時候
 * 註冊無關。所以這裡也不需要 startedAt，簽名裡沒有它是刻意的。
 *
 * 無條件進位，與 membershipDaysLeft 一致：剩半天要顯示「還剩 1 天」，
 * 顯示成 0 會讓一個還能投標的人以為自己已經不能投。
 */
export function trialDaysLeft(
  membership: Pick<Membership, "startedAt" | "trialEndedAt"> | null | undefined,
  now = new Date(),
): number {
  /*
    沒有會員資料就沒有免費期。

    這不是「還沒開始算」，是「這個人不是接案者」—— 發案方
    沒有 membership，而他們本來就永遠免費，不走這條路。
  */
  if (!membership?.startedAt) return 0;

  const started = new Date(membership.startedAt).getTime();
  /*
    startedAt 壞掉時回 0，不是回 TRIAL_DAYS。

    NaN 的算術會安靜地產生 NaN，而 NaN > 0 是 false ——
    那條路徑會把人擋在外面而不是放進來，方向是對的：
    一個資料壞掉的帳號不該因此得到無限的免費期。
  */
  if (Number.isNaN(started)) return 0;

  /*
    截止日之後才註冊的人沒有免費期。

    這是兩個機制的分工：截止日管「誰有資格」，TRIAL_DAYS 管
    「拿到多少」。少了這一條的話，90 天會變成一個永久的
    新戶優惠 —— 那是另一個產品決定，不是這一個。
  */
  if (started >= new Date(LAUNCH_SIGNUP_ENDS_AT).getTime()) return 0;

  /*
    接到案子就結束。

    這個判斷放在天數計算**之前**，因為它是一個終局狀態：一旦有值，
    剩下幾天已經沒有意義了。放在後面用 Math.min 之類的算，
    會讓「已經結束」和「剛好還剩 0 天」變成同一件事 ——
    而它們在畫面上要說的話不一樣。
  */
  if (membership.trialEndedAt) return 0;

  const ends = started + TRIAL_DAYS * 86_400_000;
  const ms = ends - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

/**
 * 能不能投標，以及不能的原因。
 *
 * 這是全站唯一判斷投標資格的地方 —— 前端用它決定要不要畫出表單，
 * contracts 服務用它決定要不要收下提案。兩邊用同一個函式，
 * 才不會出現「畫面說可以、送出說不行」。
 *
 * 檢查順序有意義：先看信箱驗證。沒驗證的人就算付了錢也不該投標 ——
 * 一個沒驗證的信箱背後不一定有人，而提案是會佔掉名額的。
 */
export type BiddingDecision =
  | { can: true; via: "membership"; daysLeft: number }
  /** 首次繳費階段：買的是五次成交，沒有到期日 */
  | { can: true; via: "first_purchase"; winsLeft: number }
  /**
   * 開站優惠的免費期 —— 不消耗額度。
   * 帶 daysLeft 是因為這一版的免費**有**到期日，畫面必須說得出還剩幾天。
   */
  | { can: true; via: "launch"; daysLeft: number }
  /*
    證件還沒驗，但額度還沒用完。

    帶 left 是因為畫面上必須說得出還剩幾次 —— 「你還可以再投幾次」
    是一句沒有內容的話，使用者無法據此決定要不要現在去驗證。
  */
  | { can: true; via: "pre_id"; left: number }
  | {
      can: false;
      reason: "none" | "unverified" | "phone_unverified" | "id_unverified" | "pending_approval" | "exhausted" | "expired";
    };

export function decideBidding(
  membership: Membership | null | undefined,
  now = new Date(),
): BiddingDecision {
  if (!membership) return { can: false, reason: "none" };

  // emailVerified 沒有帶（舊資料）時視為已驗證，避免既有會員突然被鎖住
  if (membership.emailVerified === false) return { can: false, reason: "unverified" };

  /*
   * 手機驗證。

   * 判斷放在信箱之後 —— 兩個都沒過時要先叫他去驗信箱，那是流程的第一步，
   * 先講手機只會讓他在一個還不能用的畫面前卡住。
   *
   * 同樣用 === false：沒帶的舊資料視為通過，既有會員不會在部署當下
   * 突然全部被鎖住。要對既有會員生效是另一個決定（回填 + 通知），
   * 不該由一次部署順便完成。
   */
  if (REQUIRE_PHONE_VERIFICATION && membership.phoneVerified === false) {
    return { can: false, reason: "phone_unverified" };
  }

  /*
   * 證件排在最後。
   *
   * 三道都沒過的人應該先被叫去驗信箱 —— 那是最快的一道，
   * 而且另外兩道都要求它先完成。順序反過來的話，
   * 一個什麼都還沒做的人第一眼看到的是「請上傳證件並等待審核」，
   * 那是流失率最高的一句話。
   */
  if (REQUIRE_ID_VERIFICATION && membership.idVerified === false) {
    /*
      證件還沒過的人不是直接擋掉，先給一個額度。

      理由寫在 PRE_ID_ALLOWANCE 上面：站方是一到兩個人，
      「證件審核擋在門口」在這個規模下會變成佇列，而佇列會變成流失。

      額度用完才擋 —— 而那時候擋得住的是一個已經在用平台的人，
      他會去驗證，因為他已經知道自己在等什麼。
    */
    const used = membership.preIdUsed ?? 0;
    if (used >= PRE_ID_ALLOWANCE) return { can: false, reason: "id_unverified" };

    /*
      這裡直接回 can: true 而不是往下走會員狀態的判斷。

      因為這個額度的用途是「先試試看」，而試用的人多半還在
      開站優惠期內；就算不在，讓他把三次用完再一起面對
      「要驗證證件」和「要繳會費」兩件事，比一次丟兩道牆好。
    */
    return { can: true, via: "pre_id", left: PRE_ID_ALLOWANCE - used };
  }

  /*
    ── 第四關：人工核准 ──────────────────────────────────────

    三關驗證回答「這個人是不是真的存在」，這一關回答「站方讓不讓
    他進來」—— 見 identity/migrations/0032。三關全部可以被一個要來
    詐騙的人通過：真實的信箱、預付卡門號、一張真的身分證。

    擺在證件之後，因為使用者要做的事有先後：先說「還缺證件」再說
    「等我們看」，他一次只處理一件事。

    用 === false 而不是 !approved：舊的 session 沒帶這一欄時視為
    通過，和上面三關一致 —— 跨服務部署沒有原子性，中間那幾分鐘
    不該把所有人擋住。
  */
  if (membership.approved === false) return { can: false, reason: "pending_approval" };

  /*
   * 狀態的判斷交給 membershipState，不在這裡自己算 ——
   * 付費必須比優惠先看（一個已付費但優惠剛結束的人不該被判成到期），
   * 那個順序已經寫在那裡了。
   *
   * 檢查仍在信箱／手機驗證之後 —— 免費不等於不設防。
   */
  const state = membershipState(membership, now);

  if (state === "trial") {
    return { can: true, via: "launch", daysLeft: trialDaysLeft(membership, now) };
  }

  if (state === "active") {
    const wins = membership.firstPurchaseWinsLeft ?? 0;
    // 首購階段回報的是「剩幾次成交」而不是「剩幾天」——
    // 對這個階段的人來說天數是沒有意義的數字
    if (wins > 0) return { can: true, via: "first_purchase", winsLeft: wins };
    return { can: true, via: "membership", daysLeft: membershipDaysLeft(membership, now) };
  }
  return { can: false, reason: state === "expired" ? "expired" : "exhausted" };
}

/**
 * 能不能主動發起諮詢，以及不能的原因。
 * ═══════════════════════════════════════════════════════════════
 *
 * 諮詢是雙向的，但兩邊的門檻不同：
 *
 *   發案方 → 接案方   只要有帳號（並驗證信箱）—— 發包商永遠免費
 *   接案方 → 發案方   開站優惠期間免費，優惠結束後必須是付費會員
 *
 * ── 為什麼不對稱 ────────────────────────────────────────────
 *
 * 這個平台缺的是需求不是供給。發案方是把需求帶進來的人，
 * 向他們收費（或設門檻）等於向自己最稀缺的資源收費。
 *
 * 反過來，接案方主動聯絡發案方就是在爭取生意 ——
 * 那正是會員費買的東西。
 *
 * ── 為什麼接案方這裡不吃免費額度 ────────────────────────────
 *
 * 免費額度是給「投標」用的：那有名額上限、有發案方會讀完的保證。
 * 主動諮詢沒有這兩個限制，開放免費額度使用就等於開了一個
 * 每人五次的冷開發管道，而那對發案方是騷擾。
 *
 * 所以這裡要的是 active 或 trial，不是別的。
 */
export type InquiryDecision =
  | { can: true }
  /** 證件還沒驗，但額度還沒用完 —— 和投標共用同一個額度 */
  | { can: true; via: "pre_id"; left: number }
  | {
      can: false;
      reason: "guest" | "unverified" | "phone_unverified" | "id_unverified" | "pending_approval" | "membership_required";
    };

export function decideInquiry(input: {
  /** 證件驗證前已用掉的額度（投標 + 主動聯絡合計） */
  preIdUsed?: number;
  /** 發起者的身分 */
  role: "client" | "freelancer" | "both" | null;
  /** 發起者是以哪一邊的身分聯絡對方 */
  as: "client" | "freelancer";
  emailVerified: boolean;
  membership?: Membership | null;
  /** 沒帶（舊呼叫端）時視為通過，與 decideBidding 一致 */
  phoneVerified?: boolean;
  /** 同上 */
  idVerified?: boolean;
  /** 站方人工核准過。第四關，見 identity/migrations/0032 */
  approved?: boolean;
  now?: Date;
}): InquiryDecision {
  if (!input.role) return { can: false, reason: "guest" };

  // 兩邊都要驗證信箱 —— 沒有這一關，冷開發的成本是零
  if (!input.emailVerified) return { can: false, reason: "unverified" };

  /*
   * 手機驗證同樣兩邊都要。
   *
   * 主動諮詢是最容易被濫用的入口：不需要有案件，就能直接私訊到人。
   * 只擋信箱的話，批量註冊的成本仍然接近零。
   */
  if (REQUIRE_PHONE_VERIFICATION && input.phoneVerified === false) {
    return { can: false, reason: "phone_unverified" };
  }

  // 主動聯絡與投標同一組門檻 —— 兩者都是「真的要跟陌生人往來了」
  if (REQUIRE_ID_VERIFICATION && input.idVerified === false) {
    // 和投標共用同一個額度 —— 見 PRE_ID_ALLOWANCE
    const used = input.preIdUsed ?? 0;
    if (used >= PRE_ID_ALLOWANCE) return { can: false, reason: "id_unverified" };
    return { can: true, via: "pre_id", left: PRE_ID_ALLOWANCE - used };
  }

  /*
    ── 第四關：人工核准 ──────────────────────────────────────

    三關驗證回答「這個人是不是真的存在」，這一關回答「站方讓不讓
    他進來」—— 見 identity/migrations/0032。三關全部可以被一個要來
    詐騙的人通過：真實的信箱、預付卡門號、一張真的身分證。

    擺在證件之後，因為使用者要做的事有先後：先說「還缺證件」再說
    「等我們看」，他一次只處理一件事。

    用 === false 而不是 !approved：舊的 session 沒帶這一欄時視為
    通過，和上面三關一致 —— 跨服務部署沒有原子性，中間那幾分鐘
    不該把所有人擋住。
  */
  if (input.approved === false) return { can: false, reason: "pending_approval" };


  if (input.as === "client") return { can: true };

  /*
   * 投標與主動聯絡必須同進同退。
   *
   * 早一版投標改成開站免費時漏了這半邊，出現「可以免費投標，
   * 但點『聯絡發案方』會被要求付費」——同一個人、同一天、兩種答案。
   * 反過來也一樣：優惠結束了卻還能免費冷開發，等於留了一個
   * 沒人付費的管道，而冷開發騷擾的是平台最稀缺的資源。
   */
  const state = membershipState(input.membership, input.now ?? new Date());
  return state === "active" || state === "trial"
    ? { can: true }
    : { can: false, reason: "membership_required" };
}

/** 現在能不能投標。這是會員費唯一擋住的東西。 */
export function canSubmitProposal(
  membership: Membership | null | undefined,
  now = new Date(),
): boolean {
  return decideBidding(membership, now).can;
}

/** 付費會員還剩幾天（沒付費或已過期回 0），用於前端倒數提示 */
export function membershipDaysLeft(
  membership: Membership | null | undefined,
  now = new Date(),
): number {
  if (!membership?.paidUntil) return 0;
  const ms = new Date(membership.paidUntil).getTime() - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * 續約後的新到期日。
 *
 * 從「目前到期日」而不是「今天」往後加 —— 提早續約的人
 * 不該損失剩下的天數。還沒到期就續約是我們希望鼓勵的行為。
 */
export function calcPaidUntil(
  membership: Membership | null | undefined,
  cycle: BillingCycle,
  now = new Date(),
): string {
  const currentEnd = Math.max(
    now.getTime(),
    membership?.paidUntil ? new Date(membership.paidUntil).getTime() : 0,
  );

  const date = new Date(currentEnd);
  if (cycle === "yearly") date.setFullYear(date.getFullYear() + 1);
  else date.setMonth(date.getMonth() + 1);
  return date.toISOString();
}

// ══════════════════════════════════════════════════════════════════
// 交易金額拆解
// ══════════════════════════════════════════════════════════════════

/**
 * 一筆報價拆成：平台抽成 + 接案者實收。
 *
 * 在會員制下 feeMinor 永遠是 0，netMinor 等於 grossMinor。
 * 這個函式保留是因為 UI 與對帳都需要一個明確的「實收多少」，
 * 而且合約要記錄成交當下的費率快照。
 */
export interface FeeBreakdown {
  grossMinor: number;
  feeBps: number;
  feeMinor: number;
  netMinor: number;
}

export function breakdownFee(
  grossMinor: number,
  _membership?: Membership | null,
  _now = new Date(),
  baseFeeBps = BASE_FEE_BPS,
): FeeBreakdown {
  const feeMinor = Math.round((grossMinor * baseFeeBps) / 10_000);
  return { grossMinor, feeBps: baseFeeBps, feeMinor, netMinor: grossMinor - feeMinor };
}

/** 成交當下要寫進合約的費率快照 */
export function effectiveFeeBps(
  _membership?: Membership | null,
  _now = new Date(),
  baseFeeBps = BASE_FEE_BPS,
): number {
  return baseFeeBps;
}

// ══════════════════════════════════════════════════════════════════
// 提案名額
// ══════════════════════════════════════════════════════════════════

/**
 * 每個案件的提案上限。
 *
 * 為什麼要有上限（這是本平台相對台灣既有平台的核心差異）：
 *
 *   104 外包網與 Tasker 最被抱怨的一點，是熱門案件會累積 80–90 份提案。
 *   對接案者而言，投出去的提案有 99% 機率石沉大海，久了就不投了；
 *   對發案方而言，90 份提案根本讀不完，最後只看前 5 份 —— 兩邊都輸。
 *
 *   設上限之後：
 *     接案者知道自己的勝算（1/25 而不是 1/90），願意認真寫提案
 *     發案方拿到的是 25 份有誠意的提案，而不是 90 份罐頭訊息
 *
 *   25 這個數字的依據：一般人一次能認真比較的選項約 20–30 個，
 *   超過就會退化成「只看前幾個」。
 *
 * 在會員制下這個上限更重要：會員費是固定的，投標次數不限，
 * 沒有上限的話少數人可以用機器人洗版投滿所有案子。
 *
 * 額滿的案件不會下架 —— 它會轉成「只能被邀請」，發案方仍可主動邀人投標。
 */
export const PROPOSAL_CAP = 25;

/**
 * 發案方要在幾天內回應提案。
 * ═══════════════════════════════════════════════════════════════
 *
 * 超過就自動失效，**名額還給案件、額度還給接案者**。
 *
 * ── 為什麼要有這個機制 ─────────────────────────────────────
 *
 * 接案者付會員費買的是「提案會被讀到」。如果發案方可以永遠不回應，
 * 那句話就只是行銷詞 —— 而為「對著空氣講話的權利」收費，
 * 正是賣點數、賣名單的平台在做的事。
 *
 * 逾期回收同時解決三件事：
 *
 *   額度只在真的被讀到時才消耗 —— 賣的東西變成真的
 *   25 份名額上限有意義 —— 不回應的案子會自己把名額吐出來，
 *     否則額滿的案子會永遠額滿
 *   發案方有動機回應 —— 回應率是公開的
 *
 * 7 天的依據：夠久到讓發案方有時間比較（多數案件的提案在
 * 前 48 小時湧入），又短到接案者不會覺得石沉大海。
 */
export const PROPOSAL_RESPONSE_DAYS = 7;

/** 提案的回應期限 */
export function calcProposalExpiry(
  submittedAtIso: string,
  days = PROPOSAL_RESPONSE_DAYS,
): string {
  return new Date(new Date(submittedAtIso).getTime() + days * 86_400_000).toISOString();
}

/**
 * 距離逾期還剩幾小時。已逾期回 0。
 *
 * 用小時而不是天：最後一天顯示「還剩 1 天」會讓發案方以為
 * 還有 24 小時，實際可能只剩 2 小時。
 */
export function hoursUntilExpiry(expiresAtIso: string | null | undefined, now = new Date()): number {
  if (!expiresAtIso) return 0;
  const ms = new Date(expiresAtIso).getTime() - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / 3_600_000);
}

/**
 * 發案方的回應率。
 *
 * 分母是「已經有結果的提案」——still submitted 的不算，
 * 因為它們還在期限內，還沒有失職。把它們算進分母，
 * 會讓一個剛發案的人一開始就是 0% 回應率。
 */
export function responseRate(responded: number, expired: number): number | null {
  const settled = responded + expired;
  if (settled === 0) return null;
  return responded / settled;
}

/** 還剩幾個提案名額 */
export function proposalSlotsLeft(proposalCount: number, cap = PROPOSAL_CAP): number {
  return Math.max(0, cap - proposalCount);
}

/** 名額的緊迫程度，UI 用它決定要不要強調 */
export function proposalUrgency(
  proposalCount: number,
  cap = PROPOSAL_CAP,
): "open" | "filling" | "last_call" | "full" {
  const left = proposalSlotsLeft(proposalCount, cap);
  if (left === 0) return "full";
  if (left <= 3) return "last_call";
  if (left <= cap / 2) return "filling";
  return "open";
}
